import { CONFIG } from '@porterin/booking-flow/src/order-review/domain/configs/config';
import {
  CONFIG as AccuracyConfig,
  GaEventsLocationAccuracy,
} from '@porterin/component-library/src/location-accuracy/store/LocationAccuracyStore';
import { ExtendedAddress } from '@porterin/booking-flow/src/order-review/domain/entities/ExtendedAddress';
import { getExtendedAddressFromLatLng } from '@porterin/booking-flow/src/order-review/view/helpers/GeoCodeHelper';
import { getAppConfig } from '@porterin/component-library/src/ts-utils/analytics/fetch-config/FetchConfig';
import { captureExceptionWithOptions } from '@porterin/component-library/src/ts-utils/analytics/sentry/Sentry';
import {
  publishEvent,
  publishEventParameter,
} from '@porterin/component-library/src/ts-utils/analytics/react-ga/EventTracker';
import { isDesktop } from '@porterin/lead-flow/src/components/FareEstimateHelper';
import { getLocaleDataFromCookie } from '../../utils/LocaleHelper';

export const mapOptions: google.maps.MapOptions = {
  disableDefaultUI: true,
  clickableIcons: false,
  streetViewControl: false,
  fullscreenControl: false,
  keyboardShortcuts: false,
  zoomControl: false,
  gestureHandling: 'greedy',
};

export const getGoogleMapsHookConfig = () => {
  const appConfig = getAppConfig();

  return {
    apiKey: appConfig.google_maps_api_key,
    debounce: CONFIG.GOOGLE_MAPS_DEBOUNCE,
    options: {
      componentRestrictions: {
        country: getLocaleDataFromCookie().country.googleAutoCompletePlacesCode,
      },
      input: '',
    },
  };
};

export function handleMapDragEnd({
  mapRef,
  debounceRef,
  setIsDragging,
  setCenter,
  selectLocation,
}: {
  mapRef: React.MutableRefObject<google.maps.Map | null>;
  debounceRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
  setIsDragging: (val: boolean) => void;
  setCenter: (c: { lat: number; lng: number }) => void;
  selectLocation: (e: ExtendedAddress) => void;
}) {
  setIsDragging(false);
  const center = mapRef.current?.getCenter();
  if (!center) return;

  const latLng = { lat: center.lat(), lng: center.lng() };
  setCenter(latLng);

  if (debounceRef.current) clearTimeout(debounceRef.current);

  debounceRef.current = setTimeout(() => {
    getExtendedAddressFromLatLng(latLng)
      .then(selectLocation)
      .catch(err => console.error('Geocode error', err));
  }, 200);
}

export const handleGeolocationError = (
  err: GeolocationPositionError,
  setError: (value: React.SetStateAction<string | null>) => void,
) => {
  switch (err.code) {
    case err.PERMISSION_DENIED:
      console.log('Location permission was denied.');
      setError('Location permission was denied.');
      publishEvent({
        category: 'Location Accuracy',
        action: GaEventsLocationAccuracy.locationPermissionDenied,
      });
      break;
    case err.POSITION_UNAVAILABLE:
      console.log('Location position is unavailable.');
      setError('Location position is unavailable.');
      break;
    case err.TIMEOUT:
      console.log('Location request timed out.');
      setError('Location request timed out.');
      break;
    default:
      console.log(`Unknown error occurred (code ${err.code}).`);
      setError(`Unknown error occurred (code ${err.code}).`);
  }

  if (err.code !== 1) {
    captureExceptionWithOptions('Error in getCurrentPosition', {
      'GeolocationPositionError.message':
        err instanceof Error ? err?.message : String(err),
    });
  }
};

/**
 * Common handler to fetch user's current location.
 * This function is necessary for the location accuracy feature which allows users to:
 * 1. Automatically detect their current location for pickup/dropoff addresses
 * 2. Improve user experience by pre-filling location data
 * 3. Ensure accurate address selection in the booking flow
 *
 * The geolocation API is essential for this functionality as it provides precise
 * GPS coordinates that are then converted to human-readable addresses via geocoding.
 */
export function handleGetLocation({
  setIsLoading,
  setError,
  selectLocation,
  waypoint,
  screen_name,
}: {
  setIsLoading?: (loading: boolean) => void;
  setError?: (value: React.SetStateAction<string | null>) => void;
  selectLocation: (address: ExtendedAddress) => void;
  waypoint?: 'Pickup' | 'Drop';
  screen_name?: string;
}) {
  publishEventParameter(
    waypoint === 'Pickup'
      ? GaEventsLocationAccuracy.useCurrentLocationClick
      : GaEventsLocationAccuracy.dropLocateClicked,
    {
      category: 'Location Accuracy',
      device: isDesktop() ? 'Desktop' : 'mobile',
      screen_name: screen_name ?? '',
    },
  );

  if (!navigator.geolocation) {
    setError?.('Geolocation is not supported by your browser.');
    return;
  }

  setIsLoading?.(true);
  setError?.(null);

  const onSuccess: PositionCallback = position => {
    const { latitude, longitude } = position.coords;

    getExtendedAddressFromLatLng({ lat: latitude, lng: longitude })
      .then((result: ExtendedAddress) => {
        publishEvent({
          category: 'Location Accuracy',
          action: GaEventsLocationAccuracy.allowCurrentLocationClick,
        });
        selectLocation(result);
        setError?.(null);
      })
      .catch(() => {
        setError?.('Failed to fetch address from location');
      })
      .finally(() => {
        setIsLoading?.(false);
      });
  };

  const onError: PositionErrorCallback = err => {
    publishEvent({
      category: 'Location Accuracy',
      action: GaEventsLocationAccuracy.dontAllowCurrentLocationClick,
    });
    // pass a safe setter to avoid non-null assertion
    handleGeolocationError(err, value => setError?.(value));
    setIsLoading?.(false);
  };

  navigator.geolocation.getCurrentPosition(onSuccess, onError, {
    maximumAge: AccuracyConfig.locationAccuracyMaxAge,
  });
}
